import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { reads } from 'macro-decorators';

export interface UiTextareaSignature {
  Args: {
    disabled?: boolean;
    hasError?: boolean;
    value?: string | number | null;
    autofocus?: boolean;
    isReadonly?: boolean;
    placeholder?: string;
    id?: string;
    dir?: string;
    update?: (value: string) => void;
    focusIn?: (event: FocusEvent) => void;
    focusOut?: (event: FocusEvent) => void;
  };
  Element: HTMLTextAreaElement;
}

export default class UiTextarea extends Component<UiTextareaSignature> {
  @reads('args.isReadonly', false) isReadonly!: boolean;
  @reads('args.autofocus', false) autofocus!: boolean;
  @reads('args.disabled', false) disabled!: boolean;
  @reads('args.hasError', false) hasError!: boolean;

  get classes(): string {
    return [this.hasError ? 'error' : '', this.isReadonly ? 'readonly' : ''].join(' ').trim();
  }

  @action
  input(event: Event): void {
    const { value } = event.target as HTMLTextAreaElement;
    this.args.update?.(value);
  }

  @action
  focusIn(event: FocusEvent): void {
    this.args.focusIn?.(event);
  }

  @action
  focusOut(event: FocusEvent): void {
    this.args.focusOut?.(event);
  }

  <template>
    <textarea
      {{on "input" this.input}}
      {{on "focusin" this.focusIn}}
      {{on "focusout" this.focusOut}}
      class={{this.classes}}
      data-test-error-description={{@hasError}}
      id={{@id}}
      value={{@value}}
      disabled={{this.disabled}}
      autofocus={{this.autofocus}}
      readonly={{this.isReadonly}}
      placeholder={{@placeholder}}
      dir={{@dir}}
      data-test-ui-textarea
      ...attributes
    />
  </template>
}
